/**
 * Config cards for the question editor.
 *
 *  · QuestionCommonRules  — Required + Hidden. Shown for EVERY value type
 *    (operator, 2026-07-30), so these two never live inside a type-specific card.
 *  · QuestionFileRules    — accepted formats, multiple files, size ceiling.
 *  · QuestionStepperRules — label/helper/min/max/default, and several named rows.
 *  · QuestionYesNoRules   — starting state + read-only for the toggle type.
 */
import { type CSSProperties, type ReactNode } from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconBtn, T, Toggle } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import { NumberField } from "../../lib/NumberField";
import {
  Q_FILE_FORMATS,
  Q_FILE_SIZES,
  fileSize,
  newStepperRow,
  type QFileConfig,
  type QFileFormatId,
  type QFileSizeId,
  type QInfoConfig,
  type QInfoToneId,
  Q_INFO_TONES,
  type QSelectConfig,
  type QStepperConfig,
  type QStepperRow,
  type QYesNoConfig,
  type QYesNoStart,
  type QNumberConfig,
  type QDateConfig,
} from "./question-model";

const inp = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});
const lbl: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 };
const hint: CSSProperties = { fontSize: 11.5, color: T.soft, lineHeight: 1.45, marginTop: 5, textWrap: "pretty" };
const cardTitle: CSSProperties = { fontSize: 13, fontWeight: 700, color: T.n1 };

function Row({ on, onToggle, title, sub }: { on: boolean; onToggle: () => void; title: string; sub: string }) {
  return (
    <label onClick={onToggle} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "2px 0" }}>
      <span style={{ marginTop: 1, flexShrink: 0 }}><Toggle on={on} /></span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, color: T.n1, fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45, textWrap: "pretty" }}>{sub}</span>
      </span>
    </label>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: 10, background: alpha(T.orange1, 10), border: `1px solid ${alpha(T.orange1, 28)}`, borderRadius: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
      <span style={{ color: T.orange1, flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.warn size={13} /></span>
      <span style={{ textWrap: "pretty" }}>{children}</span>
    </div>
  );
}

/* ── Required + Hidden — every value type ─────────────────────────────────── */
export function QuestionCommonRules({
  required, hidden, onRequired, onHidden,
}: {
  required: boolean;
  hidden: boolean;
  onRequired: (next: boolean) => void;
  onHidden: (next: boolean) => void;
}) {
  const t = useT();
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.common.behaviour", "Behaviour")}</div>
      <Row
        on={required}
        onToggle={() => onRequired(!required)}
        title={t("questions.textRules.required", "Required")}
        sub={t("questions.textRules.requiredSub", "The field must be filled before the booking can be submitted. Events can override this.")}
      />
      <Row
        on={hidden}
        onToggle={() => onHidden(!hidden)}
        title={t("questions.textRules.hidden", "Hidden in booking flow")}
        sub={t("questions.textRules.hiddenSubCustomerOnly", "Left off customer booking pages. Showing it to staff who book for a customer is coming later.")}
      />
      {hidden ? (
        <div style={hint}>{t("questions.textRules.hiddenNeverRequired", "A hidden question is always optional — turning Hidden on turns Required off.")}</div>
      ) : null}
    </Card>
  );
}

/* ── Yes / no switch ──────────────────────────────────────────────────────── */
export function QuestionYesNoRules({ cfg, required, onChange }: { cfg: QYesNoConfig; required: boolean; onChange: (next: QYesNoConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QYesNoConfig>) => onChange({ ...cfg, ...patch });
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.yesno.switch", "Toggle")}</div>
      <div>
        <div style={lbl}>{t("questions.yesno.startsAs", "Starts as")}</div>
        <Dropdown
          options={[
            { value: "unset", label: t("questions.yesno.startUnset", "No answer yet"), supportingText: t("questions.yesno.startUnsetHint", "Neutral until the customer touches it") },
            { value: "no", label: t("common.no", "No"), supportingText: t("questions.yesno.startNoHint", "Off, and counts as answered") },
            { value: "yes", label: t("common.yes", "Yes"), supportingText: t("questions.yesno.startYesHint", "On, and counts as answered") },
          ]}
          value={cfg.startsAs}
          onChange={(v) => set({ startsAs: v as QYesNoStart })}
          aria-label={t("questions.yesno.startsAs", "Starts as")}
          testid="qyesno-starts-as"
          style={{ gap: 0, width: "100%" }}
        />
        <div style={hint}>{t("questions.yesno.startsAsHint", "A switch that starts on Yes or No is already answered, so Required has nothing left to enforce. Leave it on \u201cNo answer yet\u201d if the customer must actively choose.")}</div>
      </div>

      <Row
        on={cfg.readOnly}
        onToggle={() => set({ readOnly: !cfg.readOnly })}
        title={t("questions.textRules.readOnly", "Read-only")}
        sub={t("questions.yesno.readOnlySub", "The customer sees the switch and its value but cannot change it \u2014 for a state set elsewhere, such as consent captured at sign-up.")}
      />

      {cfg.readOnly && cfg.startsAs === "unset" ? (
        <Warn>{t("questions.yesno.readOnlyUnsetWarnBlocks", "Read-only with no starting answer can't be saved — the customer couldn't set it. Pick Yes or No above.")}</Warn>
      ) : null}
      {required && cfg.startsAs !== "unset" ? (
        <Warn>{t("questions.yesno.requiredPrefilledWarn", "This field is marked Required, but it opens already answered \u2014 the rule can never fail. Start it on \u201cNo answer yet\u201d if you want the customer to choose deliberately.")}</Warn>
      ) : null}
    </Card>
  );
}

/* ── File upload ──────────────────────────────────────────────────────────── */
/* ── Info text ────────────────────────────────────────────────────────────── */

/**
 * The banner treatment for a tone. Exported so the config card's swatches and
 * the live preview render from ONE definition — a preview that only approximates
 * the real thing is worse than no preview.
 *
 * Surfaces are derived from the semantic-1 tokens via alpha(): the design system
 * ships no -3/-4 surface step for green/orange/red/blue, and inventing hex
 * values would drift from the palette.
 */
export function infoToneStyle(tone: QInfoToneId): { bg: string; border: string; fg: string; icon: ReactNode } | null {
  switch (tone) {
    case "info":    return { bg: alpha(T.blue1, 10),   border: alpha(T.blue1, 30),   fg: T.blue1,   icon: <Icon.info size={14} /> };
    case "success": return { bg: alpha(T.green1, 10),  border: alpha(T.green1, 30),  fg: T.green1,  icon: <Icon.check size={14} /> };
    case "warning": return { bg: alpha(T.orange1, 10), border: alpha(T.orange1, 30), fg: T.orange1, icon: <Icon.warn size={14} /> };
    case "error":   return { bg: alpha(T.red1, 10),    border: alpha(T.red1, 30),    fg: T.red1,    icon: <Icon.warn size={14} /> };
    default:        return null; // plain — no banner chrome at all
  }
}

export function QuestionInfoRules({ cfg, onChange }: { cfg: QInfoConfig; onChange: (next: QInfoConfig) => void }) {
  const t = useT();
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.info.appearance", "Appearance")}</div>

      <div>
        <div style={lbl}>{t("questions.info.style", "Style")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {Q_INFO_TONES.map((tone) => {
            const on = cfg.tone === tone.id;
            const s = infoToneStyle(tone.id);
            return (
              /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
              <button
                key={tone.id}
                type="button"
                onClick={() => onChange({ ...cfg, tone: tone.id })}
                data-testid={`qinfo-tone-${tone.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", borderRadius: 9,
                  border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white,
                  cursor: "pointer", fontFamily: T.font, textAlign: "left",
                }}
              >
                {/* A real swatch in the tone's own colours, not a generic dot. */}
                <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: s ? s.bg : T.n6, border: `1px solid ${s ? s.border : T.n4}`, color: s ? s.fg : T.n3 }}>
                  {s ? s.icon : <span style={{ fontSize: 10, fontWeight: 700 }}>{t("questions.configCards.text", "T")}</span>}
                </span>
                <span style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: on ? T.p1 : T.n1 }}>{t(tone.labelKey, tone.label)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {cfg.tone !== "plain" ? (
        <div>
          <div style={lbl}>{t("questions.info.title", "Banner title")}</div>
          <input
            value={cfg.title ?? ""}
            onChange={(e) => onChange({ ...cfg, title: e.target.value })}
            placeholder={t("questions.info.titlePlaceholder", "Optional bold lead-in, e.g. Please note")}
            data-testid="qinfo-title"
            style={inp()}
          />
          <div style={hint}>{t("questions.info.titleHint", "Sits above the text in bold. Leave empty for body text only.")}</div>
        </div>
      ) : (
        <div style={hint}>{t("questions.info.plainHint", "Plain text renders as a paragraph with no background — the default, and what existing info-text questions use.")}</div>
      )}
    </Card>
  );
}

/* ── Dropdown ─────────────────────────────────────────────────────────────── */
export function QuestionSelectRules({ cfg, optionCount, placeholder, onPlaceholder, onChange }: { cfg: QSelectConfig; optionCount: number; placeholder: string; onPlaceholder: (next: string) => void; onChange: (next: QSelectConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QSelectConfig>) => onChange({ ...cfg, ...patch });
  // The default is the control's own resting text, and it changes with the
  // cardinality — so the field shows what the customer would ACTUALLY see if the
  // author writes nothing, rather than a generic "Placeholder" hint.
  const fallback = cfg.allowMultiple
    ? t("questions.editorPage.chooseAny", "Choose any…")
    : t("questions.editorPage.chooseOne", "Choose one…");
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.select.dropdown", "Dropdown")}</div>

      <div>
        <div style={lbl}>{t("questions.select.placeholder", "Placeholder")}</div>
        <input
          value={placeholder}
          onChange={(e) => onPlaceholder(e.target.value)}
          placeholder={fallback}
          data-testid="qselect-placeholder"
          style={inp()}
        />
        <div style={hint}>{t("questions.select.placeholderHint", "The closed dropdown's text before anything is picked. Leave empty to use the default shown here.")}</div>
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => set({ allowMultiple: !cfg.allowMultiple })}
        title={t("questions.select.allowMultiple", "Allow several selections")}
        sub={t("questions.select.allowMultipleSub", "The customer can pick more than one option. Off, it is a single choice.")}
      />
      <Row
        on={cfg.sortAlphabetically}
        onToggle={() => set({ sortAlphabetically: !cfg.sortAlphabetically })}
        title={t("questions.select.sortAlpha", "Sort options alphabetically")}
        sub={t("questions.select.sortAlphaSub", "Shown A–Z to the customer instead of the order you listed them in. Your own order below is kept.")}
      />
      {cfg.sortAlphabetically && optionCount > 1 ? (
        <div style={hint}>{t("questions.select.sortAlphaNote", "Sorting is per language — a translated list sorts by its own labels, so the order can differ between languages.")}</div>
      ) : null}
      {cfg.allowMultiple ? <SelectionLimits cfg={cfg} optionCount={optionCount} onChange={onChange} /> : null}
    </Card>
  );
}

export function QuestionFileRules({ cfg, onChange }: { cfg: QFileConfig; onChange: (next: QFileConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QFileConfig>) => onChange({ ...cfg, ...patch });
  const toggleFormat = (id: QFileFormatId) => {
    const has = cfg.formats.includes(id);
    set({ formats: has ? cfg.formats.filter((f) => f !== id) : [...cfg.formats, id] });
  };
  const groups: { id: string; label: string }[] = [
    { id: "images", label: t("questions.file.images", "Images") },
    { id: "documents", label: t("questions.file.documents", "Documents") },
  ];
  const size = fileSize(cfg.maxSize);

  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.file.upload", "Upload")}</div>

      <div>
        <div style={lbl}>{t("questions.file.acceptedTypes", "Accepted file types")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {groups.map((g) => (
            <div key={g.id}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{g.label}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Q_FILE_FORMATS.filter((f) => f.group === g.id).map((f) => {
                  const on = cfg.formats.includes(f.id);
                  return (
                    /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleFormat(f.id)}
                      data-testid={`qfile-format-${f.id}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 11px", borderRadius: 8,
                        border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white,
                        color: on ? T.p1 : T.n1, fontWeight: on ? 600 : 500, fontSize: 12.5, fontFamily: T.font, cursor: "pointer",
                      }}
                    >
                      <span style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? T.p1 : T.white, border: `1px solid ${on ? T.p1 : T.n4}`, color: T.white }}>
                        {on ? <Icon.check size={10} /> : null}
                      </span>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {cfg.formats.length === 0 ? (
          <div style={{ marginTop: 10 }}>
            <Warn>{t("questions.file.noFormatsWarn", "No type selected — the field will accept any file. Pick at least one to restrict it.")}</Warn>
          </div>
        ) : null}
      </div>

      <div style={{ height: 1, background: T.border }} />

      <div>
        <div style={lbl}>{t("questions.file.maxSize", "Maximum size")}</div>
        <Dropdown
          options={Q_FILE_SIZES.map((s) => ({ value: s.id, label: t("questions.config.fileSizeOption", "{label} · {mb} MB", { label: t(s.labelKey, s.label), mb: s.mb }) }))}
          value={cfg.maxSize}
          onChange={(v) => set({ maxSize: v as QFileSizeId })}
          aria-label={t("questions.file.maxSize", "Maximum size")}
          testid="qfile-max-size"
          style={{ gap: 0, width: "100%" }}
        />
        <div style={hint}>
          {t("questions.configCards.text2", "{arg1} {arg2} {mb} MB.", { arg1: t("questions.file.maxSizeHint", "Per file, not per booking."), arg2: t("questions.file.maxSizeCurrent", "Currently"), mb: size.mb })}
        </div>
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => set({ allowMultiple: !cfg.allowMultiple })}
        title={t("questions.file.allowMultiple", "Allow several files")}
        sub={t("questions.file.allowMultipleSub", "The customer can attach more than one file. The size limit applies to each file separately.")}
      />
    </Card>
  );
}

/* ── Stepper ──────────────────────────────────────────────────────────────── */
export function QuestionStepperRules({ cfg, onChange }: { cfg: QStepperConfig; onChange: (next: QStepperConfig) => void }) {
  const t = useT();
  const rows = cfg.rows.length ? cfg.rows : [newStepperRow("")];
  const setRow = (id: string, patch: Partial<QStepperRow>) =>
    onChange({ ...cfg, rows: rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  // Whole numbers 0–9999 — the contract's QuestionStepperRowSchema bounds. NumberField floors at 0.
  const cap = (n: number) => Math.min(9999, Math.max(0, Math.round(n)));

  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={cardTitle}>{t("questions.stepper.counts", "Counts")}</div>
        {cfg.allowMultiple ? (
          <Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => onChange({ ...cfg, rows: [...rows, newStepperRow("")] })}>
            {t("questions.stepper.addCount", "Add count")}
          </Btn>
        ) : null}
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => onChange({ ...cfg, allowMultiple: !cfg.allowMultiple })}
        title={t("questions.stepper.allowMultiple", "Allow several values")}
        sub={t("questions.stepper.allowMultipleSub", "One question holding a few separate counts — Adults, Children, and so on. Off, it is a single count.")}
      />

      {rows.map((r, i) => {
        const conflict = typeof r.min === "number" && typeof r.max === "number" && r.min > r.max;
        const defOut = typeof r.defaultValue === "number" && ((typeof r.min === "number" && r.defaultValue < r.min) || (typeof r.max === "number" && r.defaultValue > r.max));
        return (
          <div key={r.id} style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, border: `1px solid ${T.border}`, borderRadius: 8, background: T.canvas }}>
            {cfg.allowMultiple ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {t("questions.configCards.text3", "{arg1} {arg2}", { arg1: t("questions.stepper.count", "Count"), arg2: i + 1 })}
                </span>
                {rows.length > 1 ? (
                  <IconBtn size={28} label={t("common.actions.remove", "Remove")} onClick={() => onChange({ ...cfg, rows: rows.filter((x) => x.id !== r.id) })}>
                    <Icon.x size={12} />
                  </IconBtn>
                ) : null}
              </div>
            ) : null}

            <div>
              <div style={lbl}>{t("questions.stepper.label", "Label")}</div>
              <input
                value={r.label}
                onChange={(e) => setRow(r.id, { label: e.target.value })}
                placeholder={t("questions.stepper.labelPlaceholder", "e.g. Adults")}
                data-testid={`qstep-label-${i}`}
                style={inp()}
              />
            </div>

            <div>
              <div style={lbl}>{t("questions.stepper.helper", "Helper text")}</div>
              <input
                value={r.helper}
                onChange={(e) => setRow(r.id, { helper: e.target.value })}
                placeholder={t("questions.stepper.helperPlaceholder", "e.g. How many adults are coming?")}
                data-testid={`qstep-helper-${i}`}
                style={inp()}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div>
                <div style={lbl}>{t("questions.stepper.min", "Lowest")}</div>
                <NumberField value={r.min ?? 0} onChange={(n) => setRow(r.id, { min: cap(n) })} ariaLabel={t("questions.stepper.min", "Lowest")} testid={`qstep-min-${i}`} style={inp({ borderColor: conflict ? T.red1 : T.n4 })} />
              </div>
              <div>
                <div style={lbl}>{t("questions.stepper.max", "Highest")}</div>
                <NumberField value={r.max ?? 0} onChange={(n) => setRow(r.id, { max: cap(n) })} ariaLabel={t("questions.stepper.max", "Highest")} testid={`qstep-max-${i}`} style={inp({ borderColor: conflict ? T.red1 : T.n4 })} />
              </div>
              <div>
                <div style={lbl}>{t("questions.stepper.default", "Starts at")}</div>
                <NumberField value={r.defaultValue ?? 0} onChange={(n) => setRow(r.id, { defaultValue: cap(n) })} ariaLabel={t("questions.stepper.default", "Starts at")} testid={`qstep-default-${i}`} style={inp({ borderColor: defOut ? T.red1 : T.n4 })} />
              </div>
            </div>

            {conflict ? <Warn>{t("questions.stepper.rangeConflict", "Lowest is above highest — the customer could not pick any value.")}</Warn> : null}
            {defOut && !conflict ? <Warn>{t("questions.stepper.defaultOutOfRangeBlocks", "The starting value sits outside the allowed range, so this can't be saved. Move it between Lowest and Highest.")}</Warn> : null}
          </div>
        );
      })}
    </Card>
  );
}

/* ── Min / max selections — multiple-answer questions (2026-10-07) ───────── */
function SelectionLimits({ cfg, optionCount, onChange }: { cfg: QSelectConfig; optionCount: number; onChange: (next: QSelectConfig) => void }) {
  const t = useT();
  const lim = (n: number) => (n <= 0 ? null : Math.min(50, Math.round(n)));
  const bad = cfg.minSelections != null && cfg.maxSelections != null && cfg.minSelections > cfg.maxSelections;
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={lbl}>{t("questions.select.minSelections", "Pick at least")}</div>
          <NumberField value={cfg.minSelections ?? 0} onChange={(n) => onChange({ ...cfg, minSelections: lim(n) })} ariaLabel={t("questions.select.minSelections", "Pick at least")} testid="qselect-min" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
        <div>
          <div style={lbl}>{t("questions.select.maxSelections", "Pick at most")}</div>
          <NumberField value={cfg.maxSelections ?? 0} onChange={(n) => onChange({ ...cfg, maxSelections: lim(n) })} ariaLabel={t("questions.select.maxSelections", "Pick at most")} testid="qselect-max" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
      </div>
      <div style={hint}>{t("questions.select.limitsHint", "0 means no limit. Applies only when the customer picks something — use Required to demand an answer.")}</div>
      {cfg.minSelections != null && optionCount > 0 && cfg.minSelections > optionCount ? <div style={{ marginTop: 8 }}><Warn>{t("questions.select.minAboveOptions", "Pick at least is more than the number of options.")}</Warn></div> : null}
    </div>
  );
}

/* ── Radio buttons / Checkbox group (2026-10-07: select settings now valid on both) ── */
export function QuestionChoiceRules({ type, cfg, optionCount, onChange }: { type: "radio" | "checks"; cfg: QSelectConfig; optionCount: number; onChange: (next: QSelectConfig) => void }) {
  const t = useT();
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{type === "radio" ? t("questions.choice.radioTitle", "Radio buttons") : t("questions.choice.checksTitle", "Checkboxes")}</div>
      <Row
        on={cfg.sortAlphabetically}
        onToggle={() => onChange({ ...cfg, sortAlphabetically: !cfg.sortAlphabetically })}
        title={t("questions.select.sortAlpha", "Sort options alphabetically")}
        sub={t("questions.select.sortAlphaSub", "Shown A–Z to the customer instead of the order you listed them in. Your own order below is kept.")}
      />
      {type === "checks" ? <SelectionLimits cfg={cfg} optionCount={optionCount} onChange={onChange} /> : null}
    </Card>
  );
}

/* ── Number (2026-10-07) ─────────────────────────────────────────────────── */
export function QuestionNumberRules({ cfg, onChange }: { cfg: QNumberConfig; onChange: (next: QNumberConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QNumberConfig>) => onChange({ ...cfg, ...patch });
  const parse = (raw: string): number | null => { const n = parseFloat(raw); return raw.trim() === "" || Number.isNaN(n) ? null : n; };
  const bad = cfg.min != null && cfg.max != null && cfg.min > cfg.max;
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.number.title", "Number")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={lbl}>{t("questions.number.min", "Lowest allowed")}</div>
          {/* Signed and decimal, so not NumberField (which floors at 0): a text input with a decimal keypad. */}
          <input inputMode="decimal" value={cfg.min ?? ""} onChange={(e) => set({ min: parse(e.target.value.replace(/[^0-9.\-]/g, "")) })} placeholder={t("questions.number.noLimit", "No limit")} data-testid="qnum-min" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
        <div>
          <div style={lbl}>{t("questions.number.max", "Highest allowed")}</div>
          <input inputMode="decimal" value={cfg.max ?? ""} onChange={(e) => set({ max: parse(e.target.value.replace(/[^0-9.\-]/g, "")) })} placeholder={t("questions.number.noLimit", "No limit")} data-testid="qnum-max" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
      </div>
      {bad ? <Warn>{t("questions.number.rangeConflict", "Lowest is above highest — no answer could pass.")}</Warn> : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={lbl}>{t("questions.number.decimals", "Decimal places")}</div>
          <Dropdown
            options={[0, 1, 2, 3].map((n) => ({ value: String(n), label: n === 0 ? t("questions.number.wholeNumbers", "Whole numbers") : String(n) }))}
            value={String(cfg.decimals ?? 0)}
            onChange={(v) => set({ decimals: parseInt(v, 10) })}
            aria-label={t("questions.number.decimals", "Decimal places")}
            testid="qnum-decimals"
            style={{ gap: 0, width: "100%" }}
          />
        </div>
        <div>
          <div style={lbl}>{t("questions.number.unit", "Unit")}</div>
          <input value={cfg.unit ?? ""} maxLength={20} onChange={(e) => set({ unit: e.target.value })} placeholder={t("questions.number.unitPlaceholder", "e.g. credits, kg")} data-testid="qnum-unit" style={inp()} />
        </div>
      </div>
      <div style={hint}>{t("questions.number.unitHint", "The unit shows after the field. Format checks are under Validate the answer.")}</div>
    </Card>
  );
}

/* ── Date (2026-10-07) ───────────────────────────────────────────────────── */
export function QuestionDateRules({ cfg, onChange }: { cfg: QDateConfig; onChange: (next: QDateConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QDateConfig>) => onChange({ ...cfg, ...patch });
  const bad = !!(cfg.earliest && cfg.latest && cfg.earliest > cfg.latest);
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.date.title", "Date")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={lbl}>{t("questions.date.earliest", "Earliest date")}</div>
          <input type="date" value={cfg.earliest ?? ""} onChange={(e) => set({ earliest: e.target.value || null })} data-testid="qdate-earliest" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
        <div>
          <div style={lbl}>{t("questions.date.latest", "Latest date")}</div>
          <input type="date" value={cfg.latest ?? ""} onChange={(e) => set({ latest: e.target.value || null })} data-testid="qdate-latest" style={inp({ borderColor: bad ? T.red1 : T.n4 })} />
        </div>
      </div>
      {bad ? <Warn>{t("questions.date.rangeConflict", "Earliest is after latest — no date could pass.")}</Warn> : null}
      <Row
        on={!!cfg.notInPast}
        onToggle={() => set({ notInPast: !cfg.notInPast })}
        title={t("questions.date.notInPast", "No dates in the past")}
        sub={t("questions.date.notInPastSub", "The customer can't pick a day before the day they book.")}
      />
    </Card>
  );
}
